import clsx from "clsx";
import { createElement, forwardRef } from "react";

import { cssUnit, getSizes } from "../types";
import { ProgressLinear } from "../Progress";
import { getColor, getRadius, getElevation } from "../../scss";
import { createPropsFilter } from "../../utils/props";
import styles from "./Card.module.scss";

import type { CardProps } from "./types";

export const filterCardProps = createPropsFilter<CardProps>([
  "className",
  "color",
  "borderRadius",
  "borderColor",
  "borderType",
  "borderThickness",
  "tag",
  "children",
  "elevation",
  "style",
  "onClick",
  "loading"
]);

const Card = forwardRef<HTMLElement, CardProps>((props: CardProps, ref) => {
  const {
    className,
    color = "neutral-25",
    borderRadius = "1",
    borderColor = "neutral-900",
    borderType = "solid",
    borderThickness,
    tag = "div",
    children,
    elevation,
    style,
    stickyTop,
    onClick,
    loading
  } = props;

  const computedStyles = {
    backgroundColor: getColor(color),
    borderRadius: getRadius(borderRadius),
    boxShadow: getElevation(elevation),
    ...(Boolean(stickyTop) && {
      position: "sticky",
      top: `${stickyTop}px`
    }),
    border:
      borderThickness &&
      `${cssUnit(borderThickness)} ${borderType} ${getColor(borderColor)}`,
    ...getSizes(props),
    ...style
  };

  return createElement(
    tag,
    {
      ref: ref,
      style: computedStyles,
      className: clsx(
        className,
        styles["card"],
        loading && styles["card--loading"]
      ),
      onClick: onClick
    },
    <>
      {loading && (
        <ProgressLinear
          className={styles["card__progress"]}
          color={borderColor}
          style={{ backgroundColor: getColor(color) }}
        />
      )}
      {children}
    </>
  );
});

Card.displayName = "Card";

export default Card;
